import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Approach } from './pages/Approach';
import { Activities } from './pages/Activities';
import { Investing } from './pages/Investing';
import { InvestingOfficers } from './pages/InvestingOfficers';
import { ValueCreation } from './pages/ValueCreation';
import { Investments } from './pages/Investments';
import { Team } from './pages/Team';
import { News } from './pages/News';
import { Contact } from './pages/Contact';
import { Legal } from './pages/Legal';
import { Toaster } from './components/ui/sonner';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Hide global footer on homepage (homepage renders its own inside the scroll container)
function ConditionalFooter() {
  const { pathname } = useLocation();
  if (pathname === '/') return null;
  return <Footer />;
}

// Floating Return to Top button - hidden on /contact, shows after 50% scroll
function ReturnToTop() {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const halfPage = document.documentElement.scrollHeight / 2;
      setVisible(window.scrollY > halfPage - window.innerHeight);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (pathname === '/contact') return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed right-6 bottom-8 z-50 flex items-center gap-1.5 px-4 py-2 bg-abyssal-blue/80 hover:bg-abyssal-blue text-white text-xs font-bold tracking-wider uppercase transition-all duration-300 shadow-lg backdrop-blur-sm"
      style={{
        fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif",
        letterSpacing: '1px',
        fontSize: '10px',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <ChevronUp className="w-3.5 h-3.5" />
      {language === 'fr' ? 'Retour en haut' : 'Return to Top'}
    </button>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navigation />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/approach" element={<Approach />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/activities/investing" element={<Investing />} />
              <Route path="/activities/investing/officers" element={<InvestingOfficers />} />
              <Route path="/activities/value-creation" element={<ValueCreation />} />
              <Route path="/investments" element={<Investments />} />
              <Route path="/companies" element={<Investments />} />
              <Route path="/team" element={<Team />} />
              <Route path="/news" element={<News />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/legal" element={<Legal />} />
            </Routes>
          </main>
          <ConditionalFooter />
          <ReturnToTop />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;